import React from "react";
import styled, { css, keyframes } from "styled-components";
import { prop, ifProp, switchProp } from "styled-tools";

import roundedPercentages from "utils/roundedPercentages";

import selfSurvey0 from "assets/images/selfSurvey-0.svg";
import selfSurvey1 from "assets/images/selfSurvey-1.svg";
import selfSurvey2 from "assets/images/selfSurvey-2.svg";
import selfSurvey3 from "assets/images/selfSurvey-3.svg";
import selfSurvey4 from "assets/images/selfSurvey-4.svg";

/*
 * Styles
 */

/* Tooltip */

const Tooltip = styled.div`
  background: ${prop("theme.colors.gray")};
  bottom: calc(100% + 1.25rem);
  box-sizing: border-box;
  color: white;
  left: 50%;
  margin-left: -5rem;
  opacity: 0;
  padding: 0.8rem 0;
  pointer-events: none;
  position: absolute;
  text-align: center;
  transform: translateY(0.5rem);
  transition: all 750ms ${prop("theme.easings.quintOut")};
  visibility: hidden;
  width: 10rem;

  &::after {
    border-left: 0.8rem solid transparent;
    border-right: 0.8rem solid transparent;
    border-top: 0.8rem solid ${prop("theme.colors.gray")};
    content: "";
    left: 50%;
    margin-left: -0.8rem;
    position: absolute;
    top: calc(100% - 1px);
  }

  span {
    display: block;
    font-size: ${prop("theme.scale.down2")};
    letter-spacing: 0.125em;
    text-transform: uppercase;
  }
`;

/* You */

const You = styled.div`
  bottom: 0;
  left: 0;
  opacity: 0;
  position: absolute;
  right: 0;
  top: 0;
  transition: all 750ms ${prop("theme.easings.cubicOut")};
  visibility: hidden;

  &::after {
    border-radius: 50%;
    content: "";
    height: 8px;
    left: 50%;
    position: absolute;
    top: 100%;
    transform: translate(-50%, -50%);
    width: 8px;
  }

  span {
    background: ${prop("theme.colors.grayTTTT")};
    font-size: ${prop("theme.scale.down1")};
    left: 50%;
    position: absolute;
    top: calc(100%);
    transform: translateX(-50%);
  }

  @media (min-width: 50em) {
    span {
      font-size: ${prop("theme.scale.down2")};
    }
  }
`;

/* Bars */

const BarsEnter = keyframes`
  from {
    opacity: 0;
    transform: translateX(50%);
  }
`;

const Bars = styled.ol.attrs({
  style: ({ barTranslation }) => ({
    transform: `translateX(${barTranslation}%)`,
  }),
})`
  animation: ${BarsEnter} 1500ms backwards 200ms ease;
  background: ${prop("theme.colors.grayTT")};
  display: flex;
  height: 7.2vh;
  list-style: none;
  margin: 0;
  min-height: 2rem;
  padding: 0;
  position: relative;
  transition: transform 1000ms ${prop("theme.easings.cubicOut")};
  transform-origin: center;
  width: 50%;
  will-change: contents;
  z-index: 10;

  ${switchProp("size", {
    S: css`
      height: 4vh;
    `,
  })}
`;

const BarEnter = keyframes`
  from {
    transform: scale(0, 1);
  }
`;

const Bar = styled.li.attrs({
  style: ({ barWidth }) => ({
    width: barWidth,
  }),
})`
  animation: ${BarEnter} 600ms backwards 200ms ${prop("theme.easings.cubicOut")};
  order: ${prop("order")};
  position: relative;
  transition: width 750ms ${prop("theme.easings.cubicOut")} 250ms;

  ${switchProp("order", {
    0: css`
      animation-delay: 700ms;
      background-color: ${prop("theme.colors.orange")};
      transform-origin: right center;

      ${You} {
        background-image: url(${selfSurvey0});
        box-shadow: inset 0 0 0 1px #c27f5c;
        &::after {
          background: #99614d;
        }
      }
    `,
    1: css`
      background-color: ${prop("theme.colors.orangeT")};
      transform-origin: right center;

      ${You} {
        background-image: url(${selfSurvey1});
        box-shadow: inset 0 0 0 1px #cb9272;
        &::after {
          background: #b17b5d;
        }
      }
    `,
    2: css`
      background-color: ${prop("theme.colors.grayTT")};

      ${You} {
        background-image: url(${selfSurvey2});
        box-shadow: inset 0 0 0 1px #cbcbcb;
        &::after {
          background: #b5b5b5;
        }
      }
    `,
    3: css`
      background-color: ${prop("theme.colors.blueT")};
      transform-origin: left center;

      ${You} {
        background-image: url(${selfSurvey3});
        box-shadow: inset 0 0 0 1px #7f969d;
        &::after {
          background: #697f86;
        }
      }
    `,
    4: css`
      animation-delay: 700ms;
      background-color: #869ba1;
      transform-origin: left center;

      ${You} {
        background-image: url(${selfSurvey4});
        box-shadow: inset 0 0 0 1px #667d83;
        &::after {
          background: #4c656b;
        }
      }
    `,
  })}

  &:hover ${Tooltip} {
    opacity: 1;
    transform: none;
    visibility: visible;
  }

  ${ifProp(
    "selfSurvey",
    css`
      ${You} {
        opacity: 1;
        visibility: visible;
      }
    `
  )}
`;

/* Container */

const Container = styled.div`
  padding: 0;
  position: relative;

  @media (min-width: 40em) {
    padding-left: 4.8rem;
    padding-right: 4.8rem;
  }
  @media (min-width: 68em) {
    padding-left: 8rem;
    padding-right: 8rem;
  }
`;

/* Text */

const H4 = styled.h4`
  background: rgba(255, 255, 255, 0.7);
  border-radius: 0.2rem;
  font-weight: bold;
  left: 0.8rem;
  margin: 0;
  padding: 0 0.4rem;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;

  @media (min-width: 40em) {
    background: transparent;
    left: 0;
    padding: 0;
  }
`;

/*
 * Component
 */

const BarStackedChart = (props) => {
  const { selfSurvey, size, year } = props;
  let { data } = props;

  if (data[1]) {
    if (!data[5]) {
      data[5] = 0;
    }
    data = roundedPercentages(data);
  }

  const sortedData = [
    { value: data[4], label: "Strongly Disagree" },
    { value: data[3], label: "Somewhat Disagree" },
    { value: data[5], label: "Not Sure" },
    { value: data[2], label: "Somewhat Agree" },
    { value: data[1], label: "Strongly Agree" },
  ];

  let translation = 100;

  translation =
    50 -
    (sortedData[0].value + sortedData[1].value) * 0.5 +
    (sortedData[3].value + sortedData[4].value) * 0.5;
  // for (let i = 0; i <= 2; i += 1) {
  //   if (i !== 2) {
  //     translation -= sortedData[i].value;
  //   } else {
  //     translation -= sortedData[i].value * 0.5;
  //   }
  // }

  return (
    <Container>
      <H4>{year}</H4>
      <Bars barTranslation={translation} size={size}>
        {sortedData.map((datum, i) => {
          return (
            <Bar
              key={i}
              barWidth={`${datum.value}%`}
              selfSurvey={selfSurvey == i + 1}
              order={i}
            >
              <Tooltip>
                <span>{datum.label}</span>
                <strong>{datum.value} %</strong>
              </Tooltip>

              <You aria-hidden="true">
                <span>You</span>
              </You>
            </Bar>
          );
        })}
      </Bars>
    </Container>
  );
};

export default BarStackedChart;
